import { useEffect, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Clock, Mail, FileText, CheckCircle2, XCircle, RotateCcw, Upload, Trash2, ExternalLink, Eye, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TimezoneSelect } from '@/components/ui/TimezoneSelect';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import { uploadFile } from '@/api/file.api';
import {
  getDailyReportConfig,
  updateDailyReportConfig,
  resendReportRun,
  type IUpdateDailyReportConfigDto,
} from '@/api/daily-report.api';

interface Props {
  clientId: string;
}

const STATUS_CONFIG: Record<string, { color: string; icon: typeof CheckCircle2 }> = {
  Pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  Generated: { color: 'bg-blue-100 text-blue-800', icon: FileText },
  Sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  Failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
};

export function DailyReportTab({ clientId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [resendConfirmId, setResendConfirmId] = useState<string | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [pendingLogoUrl, setPendingLogoUrl] = useState<string | null | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['daily-report-config', clientId],
    queryFn: () => getDailyReportConfig(clientId),
  });

  // Edit form state
  const [form, setForm] = useState<IUpdateDailyReportConfigDto>({});
  const [recipients, setRecipients] = useState<Array<{ contactId: string; firstName: string; surname: string; email: string | null; isDailyReportRecipient: boolean }>>([]);

  useEffect(() => {
    if (config && editing) {
      setForm({
        clientCompanyNameForReports: config.clientCompanyNameForReports || '',
        dailyReportEnabled: config.dailyReportEnabled,
        dailyReportSendTime: config.dailyReportSendTime || '08:00',
        dailyReportTimezone: config.dailyReportTimezone || config.timezone || '',
        dailyReportSendDaysOfWeek: config.dailyReportSendDaysOfWeek || '1,2,3,4,5',
        dailyReportEmailTemplateId: config.dailyReportEmailTemplateId,
      });
      setRecipients(
        config.contacts.map((c) => ({
          contactId: c.id,
          firstName: c.firstName,
          surname: c.surname,
          email: c.emailAddress,
          isDailyReportRecipient: c.isDailyReportRecipient,
        })),
      );
      // Reset logo state
      setLogoPreview(config.logoUrl);
      setPendingLogoUrl(undefined);
    }
  }, [config, editing]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateDailyReportConfig(clientId, {
        ...form,
        ...(pendingLogoUrl !== undefined ? { logoUrl: pendingLogoUrl } : {}),
        recipients: recipients.map((r) => ({
          contactId: r.contactId,
          isDailyReportRecipient: r.isDailyReportRecipient,
        })),
      }),
    onSuccess: () => {
      toast({ title: 'Daily report config saved' });
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['daily-report-config', clientId] });
    },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to save config'),
  });

  const resendMutation = useMutation({
    mutationFn: resendReportRun,
    onSuccess: () => {
      toast({ title: 'Resend queued' });
      setResendConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['daily-report-config', clientId] });
    },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to resend'),
  });

  /** BA §S49: Logo upload handler */
  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate image type
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please select an image file (PNG, JPG, etc.)', variant: 'destructive' });
      return;
    }

    // Preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result && typeof ev.target.result === 'string') {
        setLogoPreview(ev.target.result);
      }
    };
    reader.readAsDataURL(file);

    // Upload to R2
    setLogoUploading(true);
    try {
      const publicUrl = await uploadFile(file, `clients/${clientId}/logo`);
      setPendingLogoUrl(publicUrl);
      toast({ title: 'Logo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: (err as Error).message, variant: 'destructive' });
      setLogoPreview(config?.logoUrl || null);
      setPendingLogoUrl(undefined);
    } finally {
      setLogoUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  /** BA §S49: Logo remove handler */
  function handleLogoRemove() {
    setLogoPreview(null);
    setPendingLogoUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (isLoading) {
    return <p className="text-muted-foreground py-4">Loading daily report config...</p>;
  }
  if (!config) {
    return <p className="text-destructive py-4">Failed to load config.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Config Section */}
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Daily Report Configuration
          </h3>
          {!editing && (
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit Config
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4 max-w-lg">
            {/* BA §S49: Enable toggle — disable when whitelist is empty (E407) */}
            <div className="flex items-center justify-between">
              <Label>Enable Daily Report</Label>
              {config.availableTemplates.length === 0 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Switch checked={false} disabled />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs text-xs">
                      No email templates are allowed for Daily Report yet. Please contact your Super Admin.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Switch
                  checked={form.dailyReportEnabled || false}
                  onCheckedChange={(checked) => setForm({ ...form, dailyReportEnabled: checked })}
                />
              )}
            </div>

            {/* BA §S49: When OFF → all fields below disabled (grayed out) */}
            <fieldset disabled={!form.dailyReportEnabled} className={!form.dailyReportEnabled ? 'opacity-50 pointer-events-none' : ''}>
            {/* Company Name for Reports */}
            <div className="space-y-1">
              <Label htmlFor="clientCompanyNameForReports">Company Name (Reports)</Label>
              <Input
                id="clientCompanyNameForReports"
                value={form.clientCompanyNameForReports || ''}
                onChange={(e) => setForm({ ...form, clientCompanyNameForReports: e.target.value })}
                placeholder={config.name}
              />
              <p className="text-xs text-muted-foreground">Used in PDF header. Falls back to client name.</p>
            </div>

            {/* BA §S49: Logo Upload — preview thumbnail + Remove button */}
            <div className="space-y-2 mt-4">
              <Label>Client Logo (Reports)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              {logoPreview ? (
                <div className="w-full max-w-xs space-y-2">
                  <div
                    className="relative h-24 rounded-lg border border-border overflow-hidden flex items-center justify-center bg-white"
                  >
                    <img src={logoPreview} alt="Client logo preview" className="h-full w-full object-contain" />
                    {logoUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-xs font-medium">Uploading...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-xs"
                      disabled={logoUploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Change Logo
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 text-xs text-destructive hover:text-destructive"
                      disabled={logoUploading}
                      onClick={handleLogoRemove}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={logoUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full max-w-xs h-24 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-1.5 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="h-6 w-6 text-muted-foreground group-hover:text-primary/70 transition-colors" />
                  <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-medium">
                    Click to upload logo
                  </span>
                </button>
              )}
              <p className="text-xs text-muted-foreground">Displayed in the PDF header (left side). Recommended max 180×50px.</p>
            </div>

            {/* Send Time + Timezone */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="space-y-1">
                <Label htmlFor="dailyReportSendTime">Send Time (HH:mm)</Label>
                <Input
                  id="dailyReportSendTime"
                  value={form.dailyReportSendTime || '08:00'}
                  onChange={(e) => setForm({ ...form, dailyReportSendTime: e.target.value })}
                  placeholder="08:00"
                />
              </div>
              <div className="space-y-1">
                <Label>Timezone</Label>
                <TimezoneSelect
                  value={form.dailyReportTimezone || ''}
                  onChange={(tz) => setForm({ ...form, dailyReportTimezone: tz })}
                  placeholder={config.timezone ? `Inherited: ${config.timezone}` : 'Select timezone…'}
                />
                {(!form.dailyReportTimezone || form.dailyReportTimezone === config.timezone) && config.timezone && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-muted text-[10px] font-medium">Inherited</span>
                    from client timezone: {config.timezone}
                  </p>
                )}
              </div>
            </div>

            {/* BA [2026-05-26 rev 2]: Send Days of Week — 7 checkboxes Mon–Sun */}
            {(() => {
              const DAYS = [
                { iso: '1', label: 'Mon' },
                { iso: '2', label: 'Tue' },
                { iso: '3', label: 'Wed' },
                { iso: '4', label: 'Thu' },
                { iso: '5', label: 'Fri' },
                { iso: '6', label: 'Sat' },
                { iso: '7', label: 'Sun' },
              ];
              const selectedDays = new Set(
                (form.dailyReportSendDaysOfWeek || '1,2,3,4,5')
                  .split(',')
                  .map((d) => d.trim())
                  .filter(Boolean),
              );
              const toggleDay = (iso: string) => {
                const next = new Set(selectedDays);
                if (next.has(iso)) next.delete(iso);
                else next.add(iso);
                const sorted = ['1','2','3','4','5','6','7'].filter((d) => next.has(d));
                setForm({ ...form, dailyReportSendDaysOfWeek: sorted.join(',') });
              };
              const isEmpty = selectedDays.size === 0;
              return (
                <div className="space-y-1 mt-4">
                  <Label>Send Days of Week</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(({ iso, label }) => (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => toggleDay(iso)}
                        className={`h-8 w-12 rounded-md border text-xs font-medium transition-colors ${
                          selectedDays.has(iso)
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-background text-foreground hover:bg-accent'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {isEmpty && form.dailyReportEnabled && (
                    <p className="text-xs text-destructive mt-1">At least one day must be selected when Daily Report is enabled.</p>
                  )}
                  {isEmpty && !form.dailyReportEnabled && (
                    <p className="text-xs text-muted-foreground mt-1">Select at least one day before enabling.</p>
                  )}
                </div>
              );
            })()}

            {/* Template dropdown — BA §S49: filter by whitelist; disable + E407 when empty */}
            <div className="space-y-1 mt-4">
              <Label htmlFor="template">Email Template</Label>
              {config.availableTemplates.length === 0 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex h-9 w-full cursor-not-allowed items-center rounded-md border border-input bg-muted px-3 py-1 text-sm text-muted-foreground">
                        — No templates allowed —
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                      No email templates are allowed for Daily Report yet. Please contact your Super Admin.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div className="flex gap-2 items-start">
                  <select
                    id="template"
                    className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={form.dailyReportEmailTemplateId || ''}
                    onChange={(e) => setForm({ ...form, dailyReportEmailTemplateId: e.target.value || null })}
                  >
                    <option value="">— Select template —</option>
                    {config.availableTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.code})
                      </option>
                    ))}
                  </select>
                  {/* BA §S49: Preview button */}
                  {form.dailyReportEmailTemplateId && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1 shrink-0"
                      onClick={() => setPreviewTemplateId(form.dailyReportEmailTemplateId!)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Preview
                    </Button>
                  )}
                </div>
              )}
              {/* BA §S49: stale badge — current template no longer in whitelist */}
              {config.whitelistStaleForCurrent && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Template no longer valid — please select another
                </div>
              )}
            </div>
            </fieldset>

            {/* BA §S49: Recipients — disable checkbox + tooltip for contacts without email */}
            <div className="space-y-2">
              <Label>Recipients (Client Contacts)</Label>
              {recipients.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No contacts configured. Add contacts in the Contacts tab first.</p>
              ) : (
                <div className="space-y-1">
                  {recipients.map((r, i) => {
                    const hasEmail = !!r.email;
                    const checkboxEl = (
                      <label
                        key={r.contactId}
                        className={`flex items-center gap-3 rounded px-2 py-1.5 text-sm ${hasEmail ? 'hover:bg-accent/50 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                      >
                        <input
                          type="checkbox"
                          checked={r.isDailyReportRecipient}
                          disabled={!hasEmail}
                          onChange={(e) => {
                            const updated = [...recipients];
                            updated[i] = { ...updated[i], isDailyReportRecipient: e.target.checked };
                            setRecipients(updated);
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="font-medium">{r.firstName} {r.surname}</span>
                        <span className="text-muted-foreground">{r.email || 'No email'}</span>
                      </label>
                    );

                    if (!hasEmail) {
                      return (
                        <Tooltip key={r.contactId} delayDuration={300}>
                          <TooltipTrigger asChild>
                            {checkboxEl}
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Email required to receive reports
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return checkboxEl;
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  // BA [2026-05-26 rev 2]: block save when enabled + no days selected
                  const days = (form.dailyReportSendDaysOfWeek || '').split(',').filter(Boolean);
                  if (form.dailyReportEnabled && days.length === 0) {
                    toast({ title: 'Select at least one send day', variant: 'destructive' });
                    return;
                  }
                  saveMutation.mutate();
                }}
                disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving...' : 'Save Config'}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm max-w-lg">
            <div>
              <p className="text-muted-foreground">Status</p>
              <Badge variant={config.dailyReportEnabled ? 'default' : 'secondary'}>
                {config.dailyReportEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div>
              <p className="text-muted-foreground">Company Name (Reports)</p>
              <p className="font-medium">{config.clientCompanyNameForReports || config.name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Client Logo</p>
              {config.logoUrl ? (
                <div
                  className="mt-1 inline-flex h-16 w-44 items-center justify-center rounded-lg border border-border overflow-hidden bg-white"
                >
                  <img src={config.logoUrl} alt="Client logo" className="h-full w-full object-contain" />
                </div>
              ) : (
                <p className="text-muted-foreground italic">No logo</p>
              )}
            </div>
            <div>
              <p className="text-muted-foreground">Send Time</p>
              <p>{config.dailyReportSendTime || '08:00'} ({config.dailyReportTimezone || config.timezone || 'UTC'})</p>
            </div>
            <div>
              <p className="text-muted-foreground">Send Days</p>
              <p>{
                (() => {
                  const DAY_LABELS: Record<string, string> = { '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun' };
                  const days = (config.dailyReportSendDaysOfWeek || '1,2,3,4,5').split(',').map((d) => DAY_LABELS[d.trim()] || d).filter(Boolean);
                  return days.length > 0 ? days.join(', ') : '—';
                })()
              }</p>
            </div>
            <div>
              <p className="text-muted-foreground">Email Template</p>
              <p>{config.dailyReportEmailTemplate?.name || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground">Recipients</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {config.contacts
                  .filter((c) => c.isDailyReportRecipient)
                  .map((c) => (
                    <Badge key={c.id} variant="outline" className="text-xs">
                      {c.firstName} {c.surname} {c.emailAddress ? `<${c.emailAddress}>` : ''}
                    </Badge>
                  ))}
                {config.contacts.filter((c) => c.isDailyReportRecipient).length === 0 && (
                  <span className="text-muted-foreground italic">No recipients</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Report Runs */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Recent Report Runs
          </h3>
          {/* BA §S49: "View all" link → S50 pre-filtered by clientId */}
          <Button
            size="sm"
            variant="link"
            className="gap-1 text-xs"
            onClick={() => navigate(`/reports/email-history?clientId=${clientId}`)}
          >
            View All
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>

        {config.recentRuns.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-2">No reports generated yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Staff</th>
                  <th className="pb-2 pr-4">Leave</th>
                  <th className="pb-2 pr-4">Absent</th>
                  <th className="pb-2 pr-4">Sent At</th>
                  <th className="pb-2 pr-4">PDF</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {config.recentRuns.map((run) => {
                  const statusConf = STATUS_CONFIG[run.status] || STATUS_CONFIG.Pending;
                  const StatusIcon = statusConf.icon;
                  return (
                    <tr key={run.id} className="border-b last:border-0 hover:bg-accent/30">
                      <td className="py-2 pr-4 font-medium">{run.reportDate.split('T')[0]}</td>
                      <td className="py-2 pr-4">
                        <Badge className={`${statusConf.color} gap-1`}>
                          <StatusIcon className="h-3 w-3" />
                          {run.status}
                          {run.isTestMode && <span className="ml-1">(Test)</span>}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{run.staffCount ?? '—'}</td>
                      <td className="py-2 pr-4">{run.leaveCount ?? '—'}</td>
                      <td className="py-2 pr-4">{run.absentCount ?? '—'}</td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {run.actualSentAt ? new Date(run.actualSentAt).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 pr-4">
                        {run.pdfUrl ? (
                          <a
                            href={run.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline text-xs"
                          >
                            Download
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2">
                        {/* BA §S50: Confirm toast before resend */}
                        {run.status === 'Failed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResendConfirmId(run.id)}
                            disabled={resendMutation.isPending}
                            className="h-7 text-xs gap-1"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Resend
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BA §S49: Template Preview modal */}
      {previewTemplateId && (() => {
        const tpl = config.availableTemplates.find((t) => t.id === previewTemplateId);
        if (!tpl) return null;
        const SAMPLE: Record<string, string> = {
          '{{ClientName}}': config.clientCompanyNameForReports || config.name,
          '{{ReportDate}}': new Date().toISOString().split('T')[0],
          '{{StaffCount}}': '24',
          '{{LeaveCount}}': '3',
          '{{AbsentCount}}': '1',
        };
        const applyTags = (s: string) => Object.entries(SAMPLE).reduce((r, [k, v]) => r.split(k).join(v), s);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
                <div>
                  <h2 className="font-semibold text-sm">{tpl.name}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Subject: {applyTags(tpl.subject)}</p>
                </div>
                <button
                  onClick={() => setPreviewTemplateId(null)}
                  className="p-1 rounded hover:bg-accent transition-colors"
                  aria-label="Close preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  srcDoc={applyTags(tpl.body)}
                  title="Email preview"
                  className="w-full h-full border-0"
                  style={{ minHeight: 500 }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* BA §S50: Confirm dialog before resend */}
      <Dialog open={!!resendConfirmId} onOpenChange={(open) => { if (!open) setResendConfirmId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Resend</DialogTitle>
            <DialogDescription>
              Are you sure you want to resend this daily report? The email will be queued for immediate delivery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendConfirmId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => { if (resendConfirmId) resendMutation.mutate(resendConfirmId); }}
              disabled={resendMutation.isPending}
            >
              {resendMutation.isPending ? 'Sending...' : 'Yes, Resend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
